// src/constants/apiEndpoints.js
// *******************************************************************************
// CENTRALIZED API ENDPOINTS
// *******************************************************************************
// Centralized API endpoint definitions to improve maintainability
// = requires authentication
//
// Usage examples:
//   Simple: axiosService.get({ url: API.AUTH.SIGNIN })
//   With version: axiosService.get({ url: apiUrl.build({ url: API.AUTH.SIGNIN, version: 3 }) })
//   With params: axiosService.get({ url: apiUrl.build({ url: API.TOOLS.GET_QUOTE, params: { author: 'einstein' } }) })
//   With query: axiosService.get({ url: apiUrl.build({ url: API.ENTITIES.LIST, query: { page: 1, limit: 10 } }) })
// *******************************************************************************

import { mergeUrlParamsQuery } from 'utils/urlUtils';
import { trackApi } from 'services/apiTracker';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const DEFAULT_VERSION = 1;
// getbyfield params converted to query
// const GET_BY_FIELD_PARAMS = 'get-by-field/:field/:value/:mode?/:path?';
const ADD_TO_SET_PARAMS = 'add-to-set/:_id/:operator/:path';

// *******************************************************************************
// API ENDPOINTS
// *******************************************************************************
/**
 * Centralized API endpoints organized by domain
 * - URLs include explicit versions for documentation (e.g., /v3/auth/signin)
 * - Use apiUrl.build() to override version, replace parameters, and add query strings
 * - :param = required parameter (throws error if missing)
 * - :param? = optional parameter (removed if not provided)
 */
const API = {
  TOOLS: {
    FILE_BASE64: '/v1/tools/file/base64', //
    GET_QUOTE: '/v1/tools/get-quote/:author?', // Optional author parameter
    GET_SERVER_VERSION: '/v1/tools/get-server-version',
  },

  NIPC_SEARCH: {
    SEARCH: '/v1/nipc-search/search',
  },

  POSTAL_PT_SEARCH: {
    SEARCH: '/v1/postal-pt-search/search',
  },

  AUTH: {
    ACCOUNT: '/v1/auth/:_id?',
    SIGNIN: '/v1/auth/signin',
    SIGNIN_BY_TOKEN: '/v1/auth/signin-by-token', //
    SIGNUP: '/v1/auth/signup',

    SEND_EMAIL_REGISTRATION_CODE: '/v1/auth/send-email-registration-code',
    VALIDATE_EMAIL_CODE: '/v1/auth/validate-email-code',

    SEND_SMS_REGISTRATION_CODE: '/v1/auth/send-sms-registration-code',
    VALIDATE_SMS_CODE: '/v1/auth/validate-sms-code',

    SEND_EMAIL_RESET_CODE: '/v1/auth/send-email-reset-code',
    VALIDATE_RESET_CODE: '/v1/auth/validate-reset-code',
    RESET_PASSWORD: '/v1/auth/reset-password',
  },

  ACCOUNTS: {
    URL: `/v1/accounts/:_id?`,
    ADD_TO_SET: `/v1/accounts/${ADD_TO_SET_PARAMS}`,
    GET_BY_FIELD: `/v1/invitations/get-by-field`,
  },

  INVITATIONS: {
    URL: `/v1/invitations/:_id?`,
    POST_INVITATION: `/v1/invitations`,
    RESEND_INVITATION: `/v1/invitations/resend-invitation`,
    REMOVE_INVITATION: `/v1/invitations/:_id/:subscriptionId`,
    // GET_BY_FIELD: `/v1/invitations/${GET_BY_FIELD_PARAMS}`,
    GET_BY_FIELD: `/v1/invitations/get-by-field`,
    GET_USER_BY_EMAIL: `/v1/invitations/get-user-by-email/:email`,
  },

  SUBSCRIPTIONS: {
    URL: `/v1/subscriptions/:_id?`,
    GET_ONLY_OWNER: `/v1/subscriptions/only-owner/:_id?`,
    REMOVE_USER: `/v1/subscriptions/:_id/remove-user`,
    // Team member management: PUT updates profile/granted for an accepted member
    UPDATE_TEAM_MEMBER: `/v1/subscriptions/:subscriptionId/team/:accountId`,
    LINK_ENTITY: `/v1/subscriptions/:subscriptionId/team/:accountId/link-entity`,
    GET_TEAM: `/v1/subscriptions/:subscriptionId/team`,
  },

  // Parameters endpoints (v1)
  PARAMETERS: {
    STREAM: '/v1/parameters/stream', // SSE stream for real-time parameter updates
    GET: '/v1/parameters', // REST fallback for parameters
    UPDATE: '/v1/parameters', //
  },

  // Entity endpoints (v1)
  ENTITIES: {
    URL: '/v1/entities/:_id?', //
    PAGINATION: '/v1/entities/pagination', //
    GET_BY_FIELD: '/v1/entities/get-by-field', // GET - Search by field
    DETAIL: '/v1/entities/:_id', //
    VALIDATE_IE: '/v1/entities/ie/validate', //
    POWER_SEARCH: '/v1/entities/power-search', // POST - Fast autocomplete search in indexFields
    ADD_TO_SET: `/v1/entities/${ADD_TO_SET_PARAMS}`, //
    DOCUMENT_PROCESS: '/v1/entities/document-process', // POST - Batch archive/remove/restore
    SEARCH_BY_DOCUMENT: '/v1/entities/search-by-document', // GET - Search by document (CNPJ, CPF)
  },

  // Sticks endpoints (v1) - Personal sticky notes mural
  STICKS: {
    URL: '/v1/sticks/:_id?', // GET/POST/PUT/DELETE - CRUD
    ALL: '/v1/sticks/all', // GET - All sticks for current user
    REORDER: '/v1/sticks/reorder', // PUT - Reorder sticks (bulk)
  },

  // Agendas endpoints (v1) - Personal calendar events
  AGENDAS: {
    URL: '/v1/agendas/:_id?', // GET/POST/PUT/DELETE - CRUD
    ALL: '/v1/agendas/all', // GET - All events for current user
  },

  // CRM endpoints (v1)
  CRM: {
    URL: '/v1/crm/:_id?', // GET/POST/PUT/DELETE - CRUD
    PAGINATION: '/v1/crm/pagination', // GET - Paginated list
    GET_BY_UNIQUE_KEY: '/v1/crm/get-by-unique-key', // GET - Get by unique key
    ADD_TO_SET: `/v1/crm/${ADD_TO_SET_PARAMS}`, // PUT - Array operations
    DOCUMENT_PROCESS: '/v1/crm/document-process', // POST - Batch archive/remove/restore
    BOARD: '/v1/crm/board/:boardUid', // GET - Board view (cards grouped by columnUid)
    MOVE: '/v1/crm/move/:_id', // PUT - Move opportunity between columns
    START_ATTENDANCE: '/v1/crm/start-attendance/:_id', // PUT - Start attendance
    FILE: '/v1/crm/file/:_id', // PUT - File (archive) opportunity
    RESCHEDULE: '/v1/crm/reschedule/:_id', // PUT - Reschedule opportunity attendance
    IA_DESCRIBE: '/v1/crm/ia/describe-opportunity', // POST - AI-generated opportunity description from lead
  },

  // Product endpoints (v1)
  PRODUCTS: {
    URL: '/v1/products/:_id?', //
    PAGINATION: '/v1/products/pagination', //
    ADD_TO_SET: `/v1/products/${ADD_TO_SET_PARAMS}`,
    GET_BY_FIELD: `/v1/products/get-by-field`,
    POWER_SEARCH: '/v1/products/power-search', // POST - Fast autocomplete search in indexFields
    SEARCH_BY_SUPPLIER_SKU: '/v1/products/search-by-supplier-sku', // GET - Find product by supplier ref
    DOCUMENT_PROCESS: '/v1/products/document-process', // POST - Batch archive/remove/restore
    // AI operations for products
    IA_CATALOG: '/v1/products/ia/catalog', // POST - Catalog product with AI
    IA_ENRICH: '/v1/products/enrich-by-ai', // POST - Enrich product with AI
    IA_PRICES: '/v1/products/ia/prices', // GET - Get AI model prices
  },

  // Stock movements (inventory tracking)
  STOCK_MOVEMENTS: {
    BY_PRODUCT: '/v1/stock-movements/by-product', // GET - Movements for a product
    MANUAL: '/v1/stock-movements/manual', // POST - Create manual stock movement
    MANUAL_UPDATE: '/v1/stock-movements/manual/:_id', // PUT - Update manual stock movement
  },

  // Offer endpoints (v1) - Commercial offers for CRM/funnel
  OFFERS: {
    URL: '/v1/offers/:_id?', //
    PAGINATION: '/v1/offers/pagination', //
    ADD_TO_SET: `/v1/offers/${ADD_TO_SET_PARAMS}`,
    GET_BY_FIELD: '/v1/offers/get-by-field',
    DOCUMENT_PROCESS: '/v1/offers/document-process', // POST - Batch archive/remove/restore
  },

  // Lead endpoints (v1) - Lead capture and distribution
  LEADS: {
    URL: '/v1/leads/:_id?', //
    PAGINATION: '/v1/leads/pagination', //
    MARKETPLACE: '/v1/leads/marketplace', // GET - Available leads for agents
    ASSIGN: '/v1/leads/:_id/assign', // PUT - Supervisor assigns lead
    PURCHASE: '/v1/leads/:_id/purchase', // PUT - Agent purchases with SparkCoins
    DISCARD: '/v1/leads/:_id/discard', // PUT - Discard a lead
    FILE: '/v1/leads/:_id/file', // PUT - Archive lead after CRM distribution
    BULK_IMPORT: '/v1/leads/bulk-import', // POST - Start bulk import job
    BULK_IMPORT_EVENTS: '/v1/leads/bulk-import/events/:jobId', // GET - SSE progress
    GET_BY_FIELD: '/v1/leads/get-by-field',
  },

  // Data Sources endpoints (v1) - External lead sources (replaces LP-only routing)
  DATA_SOURCES: {
    URL: '/v1/data-sources/:_id?', //
    PAGINATION: '/v1/data-sources/pagination', //
    GET_BY_FIELD: '/v1/data-sources/get-by-field',
    // Meta Ads OAuth integration
    META_AUTH_URL: '/v1/data-sources/meta/auth-url', // POST
    META_EXCHANGE_TOKEN: '/v1/data-sources/meta/exchange-token', // POST
    META_PAGES: '/v1/data-sources/meta/pages', // POST
    META_SUBSCRIBE: '/v1/data-sources/meta/subscribe', // POST
    META_UNSUBSCRIBE: '/v1/data-sources/meta/unsubscribe', // POST
  },

  // Service endpoints (v1) - Labor/service catalog for NFSe
  SERVICES: {
    URL: '/v1/services/:_id?', //
    PAGINATION: '/v1/services/pagination', //
    ADD_TO_SET: `/v1/services/${ADD_TO_SET_PARAMS}`,
    GET_BY_FIELD: '/v1/services/get-by-field',
    DOCUMENT_PROCESS: '/v1/services/document-process', // POST - Batch archive/remove/restore
    // AI operations for services
    IA_CATALOG: '/v1/services/ia/catalog', // POST - Catalog service with AI
    IA_PRICES: '/v1/services/ia/prices', // GET - Get AI model prices
  },

  // Toolbox endpoints (v1) - Tool catalog for Apple repair
  TOOLBOX: {
    URL: '/v1/toolbox/:_id?', // GET/POST/PUT/DELETE
    PAGINATION: '/v1/toolbox/pagination', // GET - Paginated list
    ADD_TO_SET: `/v1/toolbox/${ADD_TO_SET_PARAMS}`,
    GET_BY_FIELD: '/v1/toolbox/get-by-field',
    DOCUMENT_PROCESS: '/v1/toolbox/document-process', // POST - Batch archive/remove/restore
    IA_CATALOG: '/v1/toolbox/ia/catalog', // POST - Catalog tool with AI
    IA_PRICES: '/v1/toolbox/ia/prices', // GET - Get AI model prices
  },

  // Service Order endpoints (v1) - Budgets and Service Orders
  SERVICE_ORDERS: {
    URL: '/v1/service-orders/:_id?', //
    PAGINATION: '/v1/service-orders/pagination', //
    ADD_TO_SET: `/v1/service-orders/${ADD_TO_SET_PARAMS}`,
    GET_BY_FIELD: '/v1/service-orders/get-by-field',
    DOCUMENT_PROCESS: '/v1/service-orders/document-process', // POST - Batch archive/remove/restore
    CONVERT: '/v1/service-orders/:_id/convert', // POST - Convert budget to service order
  },

  // Order endpoints (v1) - Quotes and Sales Orders
  ORDERS: {
    URL: '/v1/orders/:_id?', //
    PAGINATION: '/v1/orders/pagination', //
    ADD_TO_SET: `/v1/orders/${ADD_TO_SET_PARAMS}`,
    GET_BY_FIELD: '/v1/orders/get-by-field',
    DOCUMENT_PROCESS: '/v1/orders/document-process', // POST - Batch archive/remove/restore
    LIST: '/v1/orders',
    DETAIL: '/v1/orders/:_id',
    SEND_EMAIL: '/v1/orders/send-order-email',
  },

  // Appmulti travel-insurance order endpoints (v1) - route is SINGULAR /v1/order (Phase 1b)
  // API.ORDERS above targets the generic /v1/orders (plural) used by other modules.
  APPMULTI_ORDER: {
    URL: '/v1/order/:_id?', // POST/PUT/GET
    PAGINATION: '/v1/order/pagination', // GET paginated list
    GET_LIST: '/v1/order/get-list', // GET full list
    DETAIL: '/v1/order/:_id', // GET by id
    CANCEL_BY_ID: '/v1/order/cancel-by-id/:_id', // PUT cancel order
    SEND_EMAIL: '/v1/order/send-order-email', // POST send email
    GET_BY_BUYER_ID: '/v1/order/get-by-buyer-id/:_id', // GET by buyer
    GET_BY_TRAVELLER_ID: '/v1/order/get-by-traveller-id/:_id', // GET by traveller
    GET_BY_CUSTOMER_EMAIL: '/v1/order/get-by-customer-email/:email', // GET by email
    GET_BY_TRAVELLER_CPF: '/v1/order/get-by-traveller-cpf/:cpf', // GET by CPF
  },

  // Appmulti travel-insurance lead endpoints (v1) - route is SINGULAR /v1/lead (Phase 3)
  // API.LEADS above targets the generic /v1/leads (plural, CRM module). DISTINCT key: APPMULTI_LEADS.
  APPMULTI_LEADS: {
    URL: '/v1/lead/:_id?', // POST/PUT/GET
    PAGINATION: '/v1/lead/pagination', // GET paginated list
    GET_LIST: '/v1/lead/get-list', // GET full list
    DETAIL: '/v1/lead/:_id', // GET by id
    ADD_TO_SET: '/v1/lead/add-to-set/:_id/:operator/:path', // PUT add-to-set (followUp $push)
    GET_BY_FIELD: '/v1/lead/get-by-field/:field/:value/:projection?', // GET by field
  },

  // Receivable endpoints (v1) - bills / asaas charges
  RECEIVABLES: {
    URL: '/v1/receivables/:_id?', // POST (create) / PUT (update with :_id)
    PAGINATION: '/v1/receivables/pagination', //
    GET_BY_FIELD: '/v1/receivables/get-by-field', //
    DETAIL: '/v1/receivables/:_id', //
    CANCEL_BY_ID: '/v1/receivables/cancel-by-id/:_id', // PUT
    DOCUMENT_PROCESS: '/v1/receivables/document-process', // POST
  },

  // Asaas integration endpoints (apiKey resolved server-side - never sent by the FE)
  ASAAS: {
    PAYMENTS: '/v1/asaas/payments', // POST - generate payment URL (route confirmed T0 Step 5)
  },

  // Quotation endpoints (v1) - Travel insurance quotations (appmulti)
  QUOTATIONS: {
    URL: '/v1/quotations/:_id?', // GET/POST/PUT/DELETE - CRUD
    PAGINATION: '/v1/quotations/pagination', // GET - Paginated list
    GET_BY_FIELD: '/v1/quotations/get-by-field', // GET - Find by field
    DOCUMENT_PROCESS: '/v1/quotations/document-process', // POST - Batch archive/remove/restore
  },

  // By-Product endpoints (v1)
  BY_PRODUCTS: {
    ADD_TO_SET: `/v1/by-products/${ADD_TO_SET_PARAMS}`,
    GET_BY_FIELD: `/v1/by-products/get-by-field`,
    DOCUMENT_PROCESS: '/v1/by-products/document-process', // POST - Batch archive/remove/restore
  },



  // Attachment endpoints (v1)
  ATTACHMENTS: {
    UPLOAD: '/v1/attachments/upload', // POST - Upload file to Firebase Storage
    CHECK_DUPLICATE: '/v1/attachments/check-duplicate', // GET - Check if file exists by hash
    PERSIST: '/v1/attachments/persist', // PUT - Mark attachment(s) as persisted
    DETAIL: '/v1/attachments/:_id', // GET - Get single attachment
    DELETE: '/v1/attachments/:_id', // DELETE - Soft delete attachment
  },

  // Units Parameters endpoints (v1)
  UNITS_PARAMETERS: {
    ADD_TO_SET: `/v1/units-parameters/${ADD_TO_SET_PARAMS}`,
  },

  // Single Entity endpoints (v1) - for contact/entity operations
  // ENTITY: {
  //   CREATE: '/v1/entity',
  //   ADD_TO_SET: `/v1/entity/${ADD_TO_SET_PARAMS}`,
  // },

  // Geolocation endpoints (v1)
  // GEOLOCATION: {
  //   GET_GPS_BY_ADDRESS: '/v1/tools/geolocation/get-gps-by-address',
  // },

  // Products Parameters endpoints (v1)
  PRODUCTS_PARAMETERS: {
    ADD_TO_SET: `/v1/products/parameters/${ADD_TO_SET_PARAMS}`,
  },

  // Fiscal / Certificate endpoints (v1)
  FISCAL: {
    CERTIFICATE_VALIDATE: '/v1/fiscal/certificate/validate', // POST - Validate certificate (preview)
    CERTIFICATE_UPLOAD: '/v1/fiscal/certificate/upload', // POST - Upload and register certificate
    CERTIFICATE_DELETE: '/v1/fiscal/certificate/:entityId', // DELETE - Remove certificate
    CERTIFICATE_STATUS: '/v1/fiscal/certificate/status/:entityId', // GET - Get certificate status
    GET: '/v1/fiscal/:entityId', // GET - Get full fiscal configuration

    // Fiscal Setup (domain) - Company registration with providers
    SETUP_REGISTER: '/v1/fiscal/setup/register', // POST - Register company with provider
    SETUP_VALIDATE: '/v1/fiscal/setup/validate', // POST/GET - Validate company data
    SETUP_STATUS: '/v1/fiscal/setup/status/:entityId', // GET - Get provider status
    SETUP_VALIDATION_STATUS: '/v1/fiscal/setup/validation-status/:entityId', // GET - Get validation status per service
    SETUP_VALIDATE_SERVICE: '/v1/fiscal/setup/validate-service', // POST - Validate and activate service (nfce/nfe/nfse)
    SETUP_SYNC_CERTIFICATE: '/v1/fiscal/setup/sync-certificate', // POST - Re-sync certificate with provider
    PROVIDER_REMOVE: '/v1/fiscal/setup/provider/:entityId/:provider', // DELETE - Remove provider

    // Fiscal Service Activation - Enable/validate NFCe, NFe, NFSe
    SERVICE_VALIDATE: '/v1/fiscal/service/validate', // POST - Pre-validate service activation
    SERVICE_ACTIVATE: '/v1/fiscal/service/activate', // POST - Explicitly activate service (optional)

    // NFCe Number Voiding
    NFCE_VOID: '/v1/fiscal/nfce/void', // POST - Void NFCe number range with SEFAZ
    NFCE_CHECK_RANGE: '/v1/fiscal/nfce/check-range', // POST - Query DB for NF-Ce in number range
    NFCE_IMPORT: '/v1/fiscal/nfce/import', // POST - Import NF-Ce from SEFAZ into local DB
    NFE_VOID: '/v1/fiscal/nfe/void', // POST - Void NFe number range with SEFAZ

    // NFCe / NFe Cancelamento (backend /nfce/cancel handles both types via invoice.documentType)
    NFCE_CANCEL: '/v1/fiscal/nfce/cancel', // POST - Cancel authorized NFCe or NFe

    // NFCe Correction Letter
    NFCE_CORRECTION_LETTER: '/v1/fiscal/nfce/correction-letter', // POST - Send correction letter (CCe)

    // CNAE lookup endpoints
    CNAE_SEARCH: '/v1/fiscal/cnae/search', // GET - Search CNAE by code or description
    CNAE_GET: '/v1/fiscal/cnae/:code', // GET - Get CNAE by exact code

    // NCM lookup endpoints
    NCM_SEARCH: '/v1/fiscal/ncm/search', // GET - Search NCM by code or description
    NCM_GET: '/v1/fiscal/ncm/:code', // GET - Get NCM by exact 8-digit code

  },

  // AI endpoints (v1) - General AI operations (providers, usage stats)
  // NOTE: Module-specific AI operations are in their respective sections (PRODUCTS.IA_*, etc)
  AI: {
    PROVIDERS: '/v1/ai/providers', // GET - List AI providers and models
    USAGE: '/v1/ai/usage', // GET - Usage statistics
    USAGE_PAGINATION: '/v1/ai/usage/pagination', // GET - Usage logs paginated
    EXTRACT_CARD: '/v1/ai/extract-card', // POST - Extract contact from business card image
  },

  // WhatsApp endpoints (v1)
  WHATSAPP: {
    URL: '/v1/whatsapp/:_id?', // Base URL for CRUD operations
    SEND_MESSAGE: '/v1/whatsapp/instance/:session/send-text/:number/:message', // GET - Send WhatsApp message
    PAGINATION: '/v1/whatsapp/get-pagination', // GET - List WhatsApp connections
    VALIDATE_NUMBER: '/v1/whatsapp/validate/number', // GET - Check if phone has WhatsApp
  },

  // Data Search endpoints (v1) - CPF, CNPJ, Name, Phone, Email search with credit debit
  DATA_SEARCH: {
    SEARCH: '/v1/data-search/search', // POST - Search data with credit debit
    ENGINES: '/v1/data-search/engines', // GET - Get available engines with pricing
    URL: '/v1/data-search/:_id?', // GET/PUT/DEL - Single search result
    PAGINATION: '/v1/data-search/pagination', // GET - Search history with pagination
    LINK: '/v1/data-search/:_id/link', // POST - Link search to entity
    DOCUMENT_PROCESS: '/v1/data-search/document-process', // POST - Batch operations
  },

  MIGRATION: {
    REIMPORT: 'v1/migration/reimport-data',
    PRICES: 'v1/migration/prices', // GET - Get enrichment prices for modal display
    // Token management endpoints (all under /v1/migration/queue/)
    VALIDATE_TOKEN: 'v1/migration/queue/validate-token', // POST - Validate token and get scope info
    SAVE_TOKEN: 'v1/migration/queue/token', // POST - Save migration token (encrypted)
    UPDATE_TOKEN: 'v1/migration/queue/token', // PATCH - Update existing token (isActive, name, etc) - append /:tokenId
    DELETE_TOKEN: 'v1/migration/queue/token/:tokenId', // DELETE - Remove migration token
    // Queue operation endpoints
    QUEUE_STATUS: 'v1/migration/queue/status/:tokenId', // GET - Get migration status and progress
    QUEUE_AUTO_MIGRATE: 'v1/migration/queue/auto-migrate/:tokenId',
    QUEUE_PAUSE: 'v1/migration/queue/pause/:tokenId',
    QUEUE_RESUME: 'v1/migration/queue/resume/:tokenId',
    QUEUE_REFRESH_COUNTS: 'v1/migration/queue/refresh-counts/:tokenId',
    // Credits endpoints
    QUEUE_CREDITS_BALANCE: 'v1/migration/queue/credits-balance', // GET - Get current credits balance
    QUEUE_ESTIMATE_WITH_CREDITS: 'v1/migration/queue/estimate-with-credits/:tokenId', // GET - Estimate cost with credits conversion
  },

  // Invoice endpoints (v1) - Fiscal documents (NFe, NFCe, NFSe)
  INVOICES: {
    URL: '/v1/invoices/:_id?', // Base URL for CRUD operations
    PAGINATION: '/v1/invoices/pagination', // GET - List invoices with pagination
    GET_BY_FIELD: '/v1/invoices/get-by-field', // GET - Find invoices by field
    ADD_TO_SET: `/v1/invoices/${ADD_TO_SET_PARAMS}`, // PUT - Add to array field
    DOCUMENT_PROCESS: '/v1/invoices/document-process', // POST - Batch archive/remove/restore
    // Validation endpoint
    VALIDATE: '/v1/invoices/validate', // POST - Validate invoices before emission
    DEVOLUTION: '/v1/invoices/:_id/devolution', // POST - Create devolution from authorized NF-e
    REVERSAL: '/v1/invoices/:_id/reversal', // POST - Create reversal NF-e de Ajuste from NFC-e
    // Emission endpoints
    EMIT: '/v1/invoices/emit', // POST - Emit invoice (NFe/NFCe/NFSe)
    EMIT_NFCE: '/v1/fiscal/emit/nfce', // POST - Emit NFCe (deprecated, use EMIT)
    EMIT_NFE: '/v1/fiscal/emit/nfe', // POST - Emit NFe (deprecated, use EMIT)
    EMIT_NFSE: '/v1/fiscal/emit/nfse', // POST - Emit NFSe (deprecated, use EMIT)
    CANCEL: '/v1/fiscal/cancel/:invoiceId', // POST - Cancel invoice
    // Document URLs
    GET_PDF: '/v1/invoices/:_id/pdf', // GET - Get invoice PDF
    GET_XML: '/v1/invoices/:_id/xml', // GET - Get invoice XML
    GET_DANFE: '/v1/invoices/:_id/danfe', // GET - Get DANFE
    EXPORT_XML: '/v1/invoices/export/xml', // POST - Export XMLs as ZIP
  },

  // Ticket Events endpoints (v1) - Dynamic pricing event management (applekkus)
  TICKET_EVENTS: {
    URL: '/v1/ticket-events/:_id?', // GET/POST/PUT/DELETE - CRUD
    PAGINATION: '/v1/ticket-events/pagination', // GET - Paginated list
    CURRENT_PRICE: '/v1/ticket-events/:_id/current-price', // GET - Current price + state
    DOCUMENT_PROCESS: '/v1/ticket-events/document-process', // POST - Batch archive/remove/restore
  },

  // Ticket Sales endpoints (v1) - Ticket purchase transactions (applekkus)
  TICKET_SALES: {
    URL: '/v1/ticket-sales/:_id?', // GET/POST/PUT/DELETE - CRUD
    PAGINATION: '/v1/ticket-sales/pagination', // GET - Paginated list
    STATS: '/v1/ticket-sales/stats/:eventId', // GET - Event stats + price history
  },

  // Goods Receipt endpoints (v1) - Purchase invoice entry
  GOODS_RECEIPTS: {
    URL: '/v1/goods-receipts/:_id?', // Base URL for CRUD operations
    PAGINATION: '/v1/goods-receipts/pagination', // GET - List with pagination
    ADD_TO_SET: `/v1/goods-receipts/${ADD_TO_SET_PARAMS}`, // PUT - Add to array field
    DOCUMENT_PROCESS: '/v1/goods-receipts/document-process', // POST - Batch archive/remove/restore
    CONFIRM: '/v1/goods-receipts/:_id/confirm', // POST - Confirm entry (generate effects)
    CANCEL: '/v1/goods-receipts/:_id/cancel', // POST - Cancel entry (reverse effects)
    IMPORT: '/v1/goods-receipts/import', // POST - Import fiscal document (XML, PDF, image)
  },

  // Seed Entities endpoints (v1) - Central operator hub (appmulti)
  SYS_OPERATORS_API: {
    LIST: '/v1/seed-entities/list', // GET - List entities with replication flags
    REPLICATE: '/v1/seed-entities/replicate', // POST - Replicate entities + products to tenant
    DEACTIVATE: '/v1/seed-entities/deactivate', // POST - Deactivate (hide) entities + products
  },

  // Plan endpoints (v1) - Travel insurance plans (appmulti)
  PLANS: {
    URL: '/v1/plans/:_id?', // Base URL for CRUD operations
    PAGINATION: '/v1/plans/pagination', // GET - List plans with pagination
    GET_BY_FIELD: '/v1/plans/get-by-field', // GET - Find plans by field
    GET_BY_DESTINY: '/v1/plans/get-by-destiny', // GET - Quotation plans by destiny (qs: destiny, passengers[], days)
    ADD_TO_SET: `/v1/plans/${ADD_TO_SET_PARAMS}`, // PUT - Add to array field
    DOCUMENT_PROCESS: '/v1/plans/document-process', // POST - Batch archive/remove/restore
  },

  // Coupon endpoints (v1) - discount coupons (appmulti)
  COUPONS: {
    URL: '/v1/coupons/:_id?', // Base URL for CRUD operations
    PAGINATION: '/v1/coupons/pagination', // GET - List coupons with pagination
    GET_BY_FIELD: '/v1/coupons/get-by-field', // GET - Find coupons by field
    DOCUMENT_PROCESS: '/v1/coupons/document-process', // POST - Batch archive/remove/restore
    VALIDATE: '/v1/coupons/validate', // POST - Validate a coupon code (MG-C)
    CONSUME: '/v1/coupons/consume/:_id', // PUT - Redeem coupon (atomic usedCount++)
  },

  // Marketplace endpoints (v1) - Modules, Resources, Pricing
  MARKETPLACE: {
    CATALOG: '/v1/marketplace/catalog', // GET - Get full modules catalog
    MODULE: '/v1/marketplace/modules/:uid', // GET - Get single module details
    ACTIVATE_MODULE: '/v1/subscriptions/:subscriptionId/modules', // POST - Activate module
    DEACTIVATE_MODULE: '/v1/subscriptions/:subscriptionId/modules/:moduleUid', // DELETE - Deactivate module
    UPDATE_MODULE: '/v1/subscriptions/:subscriptionId/modules/:moduleUid', // PUT - Update module resources
    PURCHASE_CREDITS: '/v1/marketplace/credits/purchase', // POST - Purchase Jarvisly Credits
    CREDITS_BALANCE: '/v1/marketplace/credits/balance', // GET - Get credits balance
  },

  // Omnichannel endpoints (v1) - Conversation management (channel-agnostic)
  OMNICHANNEL: {
    URL: '/v1/omnichannel/:_id?', // GET/POST/PUT/DELETE - CRUD
    PAGINATION: '/v1/omnichannel/pagination', // GET - Paginated list
    ADD_TO_SET: `/v1/omnichannel/add-to-set/:_id/:operator/:path`, // PUT - Add to set (operator & path in URL)
    MARK_AS_READ: '/v1/omnichannel/mark-as-read/:_id', // PUT - Mark messages as read
    ASSIGN: '/v1/omnichannel/assign/:_id', // PUT - Assign conversation to agent
    CLOSE: '/v1/omnichannel/close/:_id', // PUT - Close conversation (status  resolved)
    DOCUMENT_PROCESS: '/v1/omnichannel/document-process', // POST - Batch archive/remove/restore
    EVENTS: '/v1/omnichannel/events', // SSE - Real-time updates stream (subscriptionId appended at call site)
    SEND_MESSAGE: '/v1/omnichannel/send-message/:_id', // POST - Send outbound message via Meta API
    TRANSFER: '/v1/omnichannel/transfer/:_id', // PUT - Transfer conversation to another agent
    CLOSE_CONTEXT: '/v1/omnichannel/close-context/:_id', // PUT - Close active context
    ESCALATE_CONTEXT: '/v1/omnichannel/escalate-context/:_id', // PUT - Escalate context to entity
    NEW_CONTEXT: '/v1/omnichannel/new-context/:_id', // POST - Open new context
    TRANSLATE: '/v1/omnichannel/translate/:_id', // POST - Translate a single message
    TRANSLATE_BATCH: '/v1/omnichannel/translate-batch/:_id', // POST - Translate all messages at once (cache-aware)
    TRANSLATE_TEXT: '/v1/omnichannel/translate-text/:_id', // POST - Translate arbitrary text (pre-send outbound)
    REFRESH_AVATAR: '/v1/omnichannel/refresh-avatar/:_id', // PUT - Force-refresh contact avatar
    SEND_REACTION: '/v1/omnichannel/react/:_id', // POST - Send emoji reaction to a message
    SYNC_ARCHIVED: '/v1/omnichannel/sync-archived', // POST - Sync archived status from Evolution API
    CLEANUP_ORPHANS: '/v1/omnichannel/cleanup-orphans', // POST - Mark orphan conversations (deleted sources)
  },

  // Omnichannel WhatsApp Config endpoints (v1) - Multi-tenant WABA phone management
  OMNICHANNEL_WHATSAPP_CONFIG: {
    URL: '/v1/omnichannel/whatsapp-config/:_id?', // GET/POST/PUT/DELETE - CRUD phones
    PROFILE: '/v1/omnichannel/whatsapp-config/:_id/profile', // GET/PUT - Profile (picture + about)
  },

  // Omnichannel Baileys Config endpoints (v1) - Unofficial WhatsApp via Evolution API
  OMNICHANNEL_BAILEYS_CONFIG: {
    URL: '/v1/omnichannel/baileys-config/:_id?', // GET/POST/PUT/DELETE - CRUD instances
    QR: '/v1/omnichannel/baileys-config/:_id/qr', // GET - Polling for QR code + status
  },

  // Omnichannel Sources (unified view of Meta + Baileys)
  OMNICHANNEL_SOURCES: {
    URL: '/v1/omnichannel/sources', // GET - List all active sources
  },

  // GoTo VoIP integration endpoints (v1)
  GOTO: {
    AUTHORIZE: '/v1/goto/authorize', // GET - Build GoTo OAuth authorize URL
    CONNECTION_STATUS: '/v1/goto/connection-status', // GET - Check GoTo connection status
    DISCONNECT: '/v1/goto/disconnect', // DELETE - Disconnect GoTo account
    USER_LINE_MAPPING: '/v1/goto/user-line-mapping', // PUT/DELETE - Map user to GoTo line
    CALLS: '/v1/goto/calls', // POST - Click-to-dial (initiate call via GoTo)
    CALLS_PAGINATION: '/v1/goto/calls/pagination', // GET - Paginated call history
    CALLS_SYNC: '/v1/goto/calls/sync', // POST - Sync stale calls with GoTo API
    CALLS_EVENTS: '/v1/goto/calls/events', // SSE - Real-time call lifecycle events
    CALLS_END: '/v1/goto/calls/:_id/end', // POST - End active call
    CALLS_AUDIO: '/v1/goto/calls/:_id/audio', // GET - Stream call recording audio (backend proxy)
  },

  // Health check
  PING: '/v1/ping',

  // External Provider APIs
  PROVIDERS: {
    // CEP (Brazilian ZIP code) lookup providers
    CEP: {
      BRASILAPI: 'https://brasilapi.com.br/api/cep/v1/:cep',
      VIACEP: 'https://viacep.com.br/ws/:cep/json/',
    },
    // GPS Coordinates lookup providers
    GPS: {
      JARVISLY: '/v1/tools/get-gps-by-address', // Internal backend (Google Maps)
      NOMINATIM: 'https://nominatim.openstreetmap.org/search', // OpenStreetMap (free, no auth)
      POSITIONSTACK: 'http://api.positionstack.com/v1/forward', // Fallback (free tier)
    },
  },
};

// *******************************************************************************
// URL BUILDER HELPERS
// *******************************************************************************
/**
 * Helpers for building URLs with dynamic parameters and query strings
 */
const apiUrl = {
  // Builds a document-process URL from a base API URL.
  // Strips pagination suffix, query params, and appends /document-process.
  documentProcess: baseUrl => {
    if (!baseUrl || typeof baseUrl !== 'string') {
      return '';
    }

    // Remove query string if present
    let url = baseUrl.split('?')[0];

    // Remove common suffixes to get base URL
    const suffixesToRemove = ['/pagination', '/:_id', '/get-by-field'];
    for (const suffix of suffixesToRemove) {
      if (url.endsWith(suffix)) {
        url = url.slice(0, -suffix.length);
        break;
      }
    }

    // Append document-process
    return `${url}/document-process`;
  },

  // Builds a URL by managing version, replacing schema parameters, and appending query strings.
  // The /api prefix is NOT added here - it is already in axios baseURL configuration.
  build: ({ url = null, version = null, params = {}, query = {} } = {}) => {
    if (!url || typeof url !== 'string') {
      return url || '';
    }

    let processedUrl = url;

    // ========================================================================
    // STEP 1: Handle API version
    // ========================================================================
    // Detect existing version in URL (e.g., /v1/, /v1/, /v3.56/)
    const versionRegex = /\/v([\d.]+)\//;
    const hasVersion = versionRegex.test(processedUrl);
    const currentVersion = hasVersion ? processedUrl.match(versionRegex)[1] : null;

    // Determine final version:
    // 1. If version param is passed, use it (override)
    // 2. Otherwise, use version from URL if exists
    // 3. Otherwise, use DEFAULT_VERSION (v1)
    const finalVersion =
      version !== null && version !== undefined ? version : currentVersion || DEFAULT_VERSION;

    // Remove existing version from URL if present
    if (hasVersion) {
      processedUrl = processedUrl.replace(versionRegex, '/');
    }

    // Insert version at the beginning
    // Note: Do NOT add /api prefix here - it's already in axios baseURL
    if (!processedUrl.startsWith('/')) {
      processedUrl = `/${processedUrl}`;
    }
    processedUrl = `/v${finalVersion}${processedUrl}`;

    // ========================================================================
    // STEP 2: Handle optional parameters (:param?)
    // ========================================================================
    // Replace them if value is provided, or remove the entire /:param? segment if not
    const optionalParamRegex = /\/:([^/?]+)\?/g;
    processedUrl = processedUrl.replace(optionalParamRegex, (match, paramName) => {
      const value = params[paramName];
      if (value !== null && value !== undefined) {
        // Replace /:param? with /value
        return `/${String(value)}`;
      } else {
        // Remove the entire /:param? segment
        return '';
      }
    });

    // Clean up any trailing slashes or double slashes that might have been created
    processedUrl = processedUrl.replace(/\/+/g, '/').replace(/\/$/, '');

    // ========================================================================
    // STEP 3: Validate required parameters (:param)
    // ========================================================================
    // Check for required parameters (/:param without ?) that are missing
    const requiredParamRegex = /:([^/?]+)(?!\?)/g;
    const missingParams = [];
    let match;

    // Reset regex index
    requiredParamRegex.lastIndex = 0;

    while ((match = requiredParamRegex.exec(processedUrl)) !== null) {
      const paramName = match[1];
      if (!params[paramName] || params[paramName] === null || params[paramName] === undefined) {
        missingParams.push(paramName);
      }
    }

    if (missingParams.length > 0) {
      throw new Error(`apiUrl.build: Missing required parameter(s): ${missingParams.join(', ')} in URL: ${url}`);
    }

    // ========================================================================
    // STEP 4: Replace remaining params and add query string
    // ========================================================================
    // Use mergeUrlParamsQuery to handle:
    // - Replacing remaining :param placeholders (non-optional)
    // - Appending query strings with proper encoding
    return mergeUrlParamsQuery({ url: processedUrl, params, query });
  },
};

// *******************************************************************************
// EXPORTS
// *******************************************************************************
export { API, apiUrl };

// *******************************************************************************
// TRACKING CASES
// *******************************************************************************

// Example: Track Parameters API
// NOTE: abort: false means log only - does NOT block the request!
trackApi(API.AUTH.SIGNUP, {
  enable: false,
  methods: ['post', 'put'],
  environment: 'development',
  abort: true, //  false = log only (does not block) | true = aborts the request
});
